import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom, forkJoin, of } from 'rxjs';
import { first, map, catchError } from 'rxjs/operators';

import { MatTabsModule } from '@angular/material/tabs';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

import { TimecardsService, TimecardAssignedRow } from '../../service/entity-services/timecards.service';
import { UsersService } from '../../service/entity-services/users.service';
import { PermissionsStoreService } from '../../service/permissions/permissions-store.service';
import { ContractInfo } from '../../objects/timecard-spec';
import { normalizeEmail } from '../../shared/utils/normalize-email';

type ActionRole = 'reviewer' | 'approver';
type ActionMode = 'approve' | 'return';

type QueueRow = TimecardAssignedRow;

/** One aggregated line in a submission's hours-by-activity breakdown. */
export interface ActivityLine {
  name: string;
  hours: number;
}

/** One shift row — what the HCP did on a given day. */
export interface ShiftDetail {
  date: string;       // dateOfWork, 'YYYY-MM-DD'
  activity: string;   // positionWorked, e.g. "3 - Other Physician"
  hours: number;      // hoursWorked
  details: string;    // activityDetails (free-text "what they did")
  facility: string;   // facilityName
  startTime: string;
  endTime: string;
}

/**
 * My Work → Timecards reviewer/approver queue. Calls GET /submissions/assigned/{callerEmail},
 * which returns the caller's actionable submissions already bucketed by the backend across
 * ALL months (no month filter — an action queue must never hide work behind a date):
 *   For My Review   — status 'submitted' and caller is the spec's approvalChain reviewer
 *   For My Approval — status 'under_review' and caller ∈ approvalChain approvers[] (incl. alternates)
 *
 * The backend owns the approvalChain resolution and HCP-name denormalization, so the web does
 * NO client-side join — it just renders the two arrays.
 *
 * Canonical state machine (unified redesign §3 — timecards renamed to match timesheets):
 *   draft ─submit─▶ submitted ─reviewer approve─▶ under_review ─approver approve─▶ approved (terminal)
 *   reviewer/approver "return" ─▶ rejected (HCP edits + resubmits);  HCP recall ─▶ draft
 * There is NO payment/accounting stage; `approved` is terminal (§4). `send_back_to_reviewer` is retired.
 *
 * Actions go through POST /approvals/review and /approvals/approve (action:'approve'|'return',
 * keyed by periodKey). The backend authorizes the caller per-actor (403), guards state (409) +
 * action/note (400), carries the note into the return email, and OWNS ALL notifications. The web
 * sends NO emails and never uses the admin PUT for queue actions.
 */
@Component({
  selector: 'app-timecard-approval-queue',
  standalone: true,
  imports: [
    CommonModule, FormsModule,
    MatTabsModule, MatCardModule, MatButtonModule, MatIconModule, MatTooltipModule,
    MatFormFieldModule, MatInputModule, MatProgressSpinnerModule, MatSnackBarModule
  ],
  templateUrl: './timecard-approval-queue.component.html',
  styleUrls: ['./timecard-approval-queue.component.css']
})
export class TimecardApprovalQueueComponent implements OnInit {
  private timecardsService = inject(TimecardsService);
  private usersService = inject(UsersService);
  private permissionsStore = inject(PermissionsStoreService);
  private snackBar = inject(MatSnackBar);

  loading = signal(true);
  acting = signal(false);
  reviewQueue = signal<QueueRow[]>([]);
  approvalQueue = signal<QueueRow[]>([]);

  /** True when a System Administrator is viewing ALL submitted timecards (not just
   *  their own assignments). Drives the banner + tab labels. */
  adminView = signal(false);

  /** key = `${principalEmail}|${periodKey}|${mode}` of the row whose note box is open. */
  activeKey = signal<string | null>(null);
  noteText = '';

  /**
   * Contracts for each HCP, keyed by normalized principal email. Joined client-side
   * from the spec list because the assigned-submissions row doesn't carry contract
   * info today (see the backend prompt — the durable fix is to denormalize it onto
   * the row). Filled in after the queue loads; best-effort.
   */
  contractsByEmail = signal<Record<string, ContractInfo[]>>({});

  /**
   * Hours-by-activity breakdown per submission, keyed by `${principalEmail}|${periodKey}`.
   * Filled in after the queue loads by fetching each HCP's timecard entries and
   * aggregating. Lets the reviewer/approver see WHAT the HCP did, not just a total.
   */
  breakdowns = signal<Record<string, ActivityLine[]>>({});

  /** Per-shift detail rows per submission, keyed the same as `breakdowns`. */
  shiftsByKey = signal<Record<string, ShiftDetail[]>>({});

  private myEmail = '';

  ngOnInit(): void {
    this.load();
  }

  async load(): Promise<void> {
    this.loading.set(true);
    this.activeKey.set(null);
    try {
      this.myEmail = normalizeEmail(await firstValueFrom(this.usersService.currentUserEmail$.pipe(first())));

      let forReview: QueueRow[];
      let forApproval: QueueRow[];

      if (this.isSystemAdmin()) {
        // System Administrator ONLY: see ALL in-flight timecards (every HCP, every
        // month), not just their own assignments. The backend admin endpoint gates
        // to System Administrator and returns the same bucketed shape as the assigned
        // endpoint, so we render it identically.
        this.adminView.set(true);
        const result = await firstValueFrom(this.timecardsService.getActiveAdminSubmissions());
        forReview = result.forReview;
        forApproval = result.forApproval;
      } else {
        this.adminView.set(false);
        if (!this.myEmail) {
          this.reviewQueue.set([]);
          this.approvalQueue.set([]);
          return;
        }
        const result = await firstValueFrom(this.timecardsService.getAssignedTimecardSubmissions(this.myEmail));
        forReview = result.forReview;
        forApproval = result.forApproval;
      }

      this.reviewQueue.set(forReview);
      this.approvalQueue.set(forApproval);
      // Fire-and-forget: cards render immediately; contract labels fill in once
      // the spec list is fetched and joined by principal email. Skip the join
      // entirely once the backend denormalizes contracts onto every row.
      this.contractsByEmail.set({});
      const allRows = [...forReview, ...forApproval];
      const needsJoin = allRows.some(r => !(Array.isArray(r.contracts) && r.contracts.length));
      if (needsJoin) this.loadContracts();

      // Fire-and-forget: cards render immediately; the shift details +
      // hours-by-activity breakdown fill in as each HCP's entries are fetched.
      this.breakdowns.set({});
      this.shiftsByKey.set({});
      this.loadBreakdowns(allRows);
    } catch (err) {
      console.error('[TimecardApprovalQueue] load failed', err);
      this.snackBar.open('Could not load the approval queue.', 'Dismiss', { duration: 4000 });
    } finally {
      this.loading.set(false);
    }
  }

  /** Only a System Administrator (NOT a plain Administrator) sees all submitted timecards. */
  private isSystemAdmin(): boolean {
    const role = (this.permissionsStore.permissions()?.role ?? '').toLowerCase().trim();
    return role === 'system administrator';
  }

  /**
   * Fetch the spec list and build a normalized-email → contracts map. Best
   * effort: if the spec-list endpoint is unavailable, cards simply render
   * without contract labels rather than failing the queue.
   */
  private async loadContracts(): Promise<void> {
    try {
      const specs = await firstValueFrom(this.timecardsService.listTimecardSpecs());
      const map: Record<string, ContractInfo[]> = {};
      for (const spec of specs ?? []) {
        const email = normalizeEmail(spec?.email ?? '');
        const contracts = Array.isArray(spec?.contracts) ? spec.contracts : [];
        if (email && contracts.length) map[email] = contracts;
      }
      this.contractsByEmail.set(map);
    } catch {
      // Leave the map empty; cards still render with name/period/hours.
    }
  }

  /**
   * Prefer contracts denormalized onto the row by the backend; fall back to the
   * client-side spec-list join until that ships. Once the backend always sends
   * `row.contracts`, the join (loadContracts/contractsByEmail) can be removed.
   */
  contractsFor(row: QueueRow): ContractInfo[] {
    if (Array.isArray(row.contracts) && row.contracts.length) return row.contracts;
    return this.contractsByEmail()[normalizeEmail(row.principalEmail)] ?? [];
  }

  /** HCP display name with fallbacks — endpoint sends hcpName, but be defensive. */
  displayName(row: QueueRow): string {
    return row.hcpName?.trim()
      || [row.firstName, row.lastName].filter(Boolean).join(' ').trim()
      || row.principalEmail;
  }

  // ── Shift details + hours-by-activity breakdown ──────────────

  private breakdownKey(row: QueueRow): string {
    return `${normalizeEmail(row.principalEmail)}|${row.periodKey}`;
  }

  /** Aggregated hours-by-activity (positionWorked) lines for a row. */
  breakdownFor(row: QueueRow): ActivityLine[] {
    return this.breakdowns()[this.breakdownKey(row)] ?? [];
  }

  /** Per-shift detail rows for a row — what the HCP did, day by day. */
  shiftsFor(row: QueueRow): ShiftDetail[] {
    return this.shiftsByKey()[this.breakdownKey(row)] ?? [];
  }

  /**
   * Total hours for a row. Prefer the backend's denormalized `totalHours`
   * (authoritative); fall back to summing the fetched shifts if it's absent.
   */
  totalHoursFor(row: QueueRow): number {
    if (row.totalHours != null) return row.totalHours;
    return this.shiftsFor(row).reduce((s, sh) => s + sh.hours, 0);
  }

  /**
   * Fetch each HCP's timecard entries for the period, then derive (a) per-shift
   * detail rows and (b) an hours-by-activity rollup. Best-effort and non-
   * blocking — a failed fetch just leaves that card with only the total.
   * De-duped by principal+period so a submission in both tabs is fetched once.
   *
   * Entry shape (GET /admin/timecards/hcp/{email}?month=YYYY-MM):
   *   { success, items: [{ hoursWorked, positionWorked, dateOfWork,
   *     facilityName, startTime, endTime, activityDetails, ... }] }
   */
  private async loadBreakdowns(rows: QueueRow[]): Promise<void> {
    const seen = new Set<string>();
    const unique = rows.filter(r => {
      if (!r.principalEmail || !r.periodKey) return false;
      const k = this.breakdownKey(r);
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    if (!unique.length) return;
    try {
      const results = await firstValueFrom(forkJoin(
        unique.map(r =>
          this.timecardsService.getHcpTimecards(r.principalEmail, r.periodKey).pipe(
            map(raw => {
              const shifts = this.extractShifts(raw);
              return { key: this.breakdownKey(r), shifts, lines: this.rollUpByActivity(shifts) };
            }),
            catchError(() => of({ key: this.breakdownKey(r), shifts: [] as ShiftDetail[], lines: [] as ActivityLine[] }))
          )
        )
      ));
      const nextLines: Record<string, ActivityLine[]> = {};
      const nextShifts: Record<string, ShiftDetail[]> = {};
      for (const { key, shifts, lines } of results) {
        nextShifts[key] = shifts;
        nextLines[key] = lines;
      }
      this.shiftsByKey.set(nextShifts);
      this.breakdowns.set(nextLines);
    } catch {
      // Leave whatever we have; cards still render with the total.
    }
  }

  /** Map the raw timecard entries to normalized per-shift rows, sorted by date. */
  private extractShifts(raw: any): ShiftDetail[] {
    const items: any[] = Array.isArray(raw?.items) ? raw.items
      : Array.isArray(raw?.Items) ? raw.Items
      : Array.isArray(raw) ? raw
      : [];
    return items.map(e => {
      const hrs = parseFloat(e?.hoursWorked);
      return {
        date: String(e?.dateOfWork ?? '').trim(),
        activity: String(e?.positionWorked ?? '').trim() || 'Shift',
        hours: isNaN(hrs) ? 0 : hrs,
        details: String(e?.activityDetails ?? '').trim(),
        facility: String(e?.facilityName ?? '').trim(),
        startTime: String(e?.startTime ?? '').trim(),
        endTime: String(e?.endTime ?? '').trim(),
      };
    }).sort((a, b) => a.date.localeCompare(b.date));
  }

  /** Roll the shifts up into hours-by-activity lines (sorted high → low). */
  private rollUpByActivity(shifts: ShiftDetail[]): ActivityLine[] {
    const totals = new Map<string, number>();
    for (const s of shifts) {
      if (!s.hours) continue;
      totals.set(s.activity, (totals.get(s.activity) || 0) + s.hours);
    }
    return [...totals.entries()]
      .map(([name, hours]) => ({ name, hours: Math.round(hours * 100) / 100 }))
      .sort((a, b) => b.hours - a.hours);
  }

  /** Format a 'YYYY-MM-DD' work date without timezone shifting. */
  formatWorkDate(d: string): string {
    const parts = (d || '').split('-').map(Number);
    if (parts.length !== 3 || parts.some(isNaN)) return d || '';
    const [y, m, day] = parts;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[m - 1]} ${day}, ${y}`;
  }

  /** Compact "time-range · facility" suffix for a shift (e.g. "08:17–15:00 · AAA Site 1"). */
  shiftMeta(s: ShiftDetail): string {
    const time = s.startTime && s.endTime ? `${s.startTime}–${s.endTime}` : (s.startTime || s.endTime || '');
    return [time, s.facility].filter(Boolean).join(' · ');
  }

  keyFor(row: QueueRow, mode: string): string {
    return `${row.principalEmail}|${row.periodKey}|${mode}`;
  }

  isOpen(row: QueueRow, mode: string): boolean {
    return this.activeKey() === this.keyFor(row, mode);
  }

  startAction(row: QueueRow, role: ActionRole, mode: ActionMode): void {
    if (mode === 'approve') {
      this.runAction(row, role, mode);
      return;
    }
    this.noteText = '';
    this.activeKey.set(this.keyFor(row, mode));
  }

  cancelAction(): void {
    this.activeKey.set(null);
    this.noteText = '';
  }

  confirmAction(row: QueueRow, role: ActionRole, mode: ActionMode): void {
    // Return is a reject (§2) — a note is required. Guard client-side so the
    // admin gets immediate feedback rather than a backend 400 NOTE_REQUIRED.
    if (mode === 'return' && !this.noteText.trim()) {
      this.snackBar.open('A note is required to return this to the owner.', 'Dismiss', { duration: 4000 });
      return;
    }
    this.runAction(row, role, mode, this.noteText);
  }

  private async runAction(row: QueueRow, role: ActionRole, mode: ActionMode, note = ''): Promise<void> {
    if (this.acting()) return;
    this.acting.set(true);
    try {
      if (role === 'reviewer') {
        await firstValueFrom(
          this.timecardsService.reviewSubmission(row.principalEmail, row.periodKey, mode, note)
        );
      } else {
        await firstValueFrom(
          this.timecardsService.approveSubmission(row.principalEmail, row.periodKey, mode, note)
        );
      }
      this.snackBar.open(this.successMessage(mode), 'Dismiss', { duration: 3000 });
      this.activeKey.set(null);
      this.noteText = '';
      await this.load();
    } catch (err: any) {
      console.error('[TimecardApprovalQueue] action failed', err);
      await this.handleActionError(err);
    } finally {
      this.acting.set(false);
    }
  }

  /**
   * Map the canonical approval-action errors to admin-friendly feedback (§6/§7):
   *   403 — caller isn't in this stage's actor list
   *   409 — STATUS_CONFLICT: someone else already acted (optimistic-write race) → reload
   *   400 NOTE_REQUIRED — return/reject without a note
   */
  private async handleActionError(err: any): Promise<void> {
    const status = err?.status;
    const code = err?.error?.code || err?.error?.errorType;
    const detail = err?.error?.message || err?.message || 'Please try again.';
    if (status === 403) {
      this.snackBar.open("You're not assigned to act on this item.", 'Dismiss', { duration: 5000 });
    } else if (status === 409) {
      this.snackBar.open('Someone already acted on this — refreshing the queue.', 'Dismiss', { duration: 5000 });
      this.activeKey.set(null);
      this.noteText = '';
      await this.load();
    } else if (status === 400 && /note/i.test(`${code} ${detail}`)) {
      this.snackBar.open('A note is required for this action.', 'Dismiss', { duration: 5000 });
    } else {
      this.snackBar.open(`Action failed. ${detail}`, 'Dismiss', { duration: 5000 });
    }
  }

  private successMessage(mode: ActionMode): string {
    switch (mode) {
      case 'approve': return 'Approved.';
      case 'return': return 'Returned to the owner.';
      default: return 'Done.';
    }
  }

  /** Round hours to 2 decimals, dropping trailing zeros (6.716666… → "6.72", 8 → "8"). */
  formatHours(n: number | undefined | null): string {
    return n == null ? '0' : String(Number(Number(n).toFixed(2)));
  }
}
